import { CaretDown, CheckCircle } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import '../../pages/Orders/OrdersPage.scss'

const MAX_RANGE_DAYS = 92 // ~3 months

const DATE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This week' },
  { value: 'last-week', label: 'Last week' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'custom', label: 'Custom date…' },
]

const normalizeToDate = (value) => {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value?.toDate === 'function') {
    const date = value.toDate()
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export default function DateRangeDropdown({ value, customRange, onChange, onCustomRange }) {
  const [open, setOpen] = useState(false)
  const [showCal, setShowCal] = useState(false)
  // leftMonth is the month shown in the LEFT calendar (Date object, day=1)
  const [leftMonth, setLeftMonth] = useState(() => {
    const d = new Date(); d.setDate(1); return d
  })
  // Draft selection while user picks
  const [draft, setDraft] = useState({ start: null, end: null, hover: null })
  const ref = useRef(null)

  const isCustom = value === 'custom'
  const isActive = Boolean(value)

  const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1)

  // Trigger label
  const triggerLabel = (() => {
    const startDate = normalizeToDate(customRange?.start)
    const endDate = normalizeToDate(customRange?.end)

    if (isCustom && startDate && endDate) {
      const fmt = (d) => d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })
      return `${fmt(startDate)} – ${fmt(endDate)}`
    }
    return DATE_OPTIONS.find((o) => o.value === value)?.label ?? 'Date range'
  })()

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setShowCal(false) } }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selectPreset = (opt) => {
    if (opt.value === 'custom') { setShowCal(true); setDraft({ start: null, end: null, hover: null }); return }
    onChange(opt.value)
    setOpen(false)
    setShowCal(false)
  }

  // ── Calendar helpers ──────────────────────────────────────────────────────

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay() // 0=Sun

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString()

  const isInRange = (day, start, end) => {
    if (!start || !end) return false
    const t = day.getTime()
    return t > Math.min(start.getTime(), end.getTime()) && t < Math.max(start.getTime(), end.getTime())
  }

  const isHoverRange = (day, start, hover) => {
    if (!start || !hover || draft.end) return false
    const t = day.getTime()
    return t > Math.min(start.getTime(), hover.getTime()) && t < Math.max(start.getTime(), hover.getTime())
  }

  const exceedsMax = (start, end) => {
    if (!start || !end) return false
    const diff = Math.abs(end - start) / (1000 * 60 * 60 * 24)
    return diff > MAX_RANGE_DAYS
  }

  const handleDayClick = (day) => {
    if (!draft.start || draft.end) {
      // Start fresh selection
      setDraft({ start: day, end: null, hover: null })
    } else {
      // Second click = end
      const start = draft.start
      const end = day
      if (exceedsMax(start, end)) return // silently block
      const s = start < end ? start : end
      const e = start < end ? end : start
      setDraft({ start: s, end: e, hover: null })
    }
  }

  const handleApply = () => {
    if (draft.start && draft.end) {
      onCustomRange({ start: moment(draft.start).format("YYYY-MM-DD"), end: moment(draft.end).format("YYYY-MM-DD") })
      onChange('custom')
      setOpen(false)
      setShowCal(false)
    }
  }

  const prevMonth = () => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1))
  const nextMonth = () => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1))

  const renderMonth = (baseDate) => {
    const year = baseDate.getFullYear()
    const month = baseDate.getMonth()
    const days = getDaysInMonth(year, month)
    const startDow = getFirstDayOfWeek(year, month)
    const monthLabel = baseDate.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })

    const cells = []
    for (let i = 0; i < startDow; i++) cells.push(null)
    for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d))

    const today = new Date(); today.setHours(0, 0, 0, 0)

    // Max end date from draft.start
    const maxEnd = draft.start && !draft.end
      ? new Date(draft.start.getTime() + MAX_RANGE_DAYS * 86400000)
      : null

    return (
      <div className="drcal__month">
        <div className="drcal__month-label">{monthLabel}</div>
        <div className="drcal__weekdays">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
            <span key={d} className="drcal__wd">{d}</span>
          ))}
        </div>
        <div className="drcal__grid">
          {cells.map((day, i) => {
            if (!day) return <span key={`e${i}`} />

            const isStart = isSameDay(day, draft.start)
            const isEnd = isSameDay(day, draft.end)
            const inRange = isInRange(day, draft.start, draft.end)
            const inHover = isHoverRange(day, draft.start, draft.hover)
            const isToday = isSameDay(day, today)
            const disabled = maxEnd ? day > maxEnd : false

            return (
              <button
                key={day.toISOString()}
                className={[
                  'drcal__day',
                  isStart ? 'drcal__day--start' : '',
                  isEnd ? 'drcal__day--end' : '',
                  inRange ? 'drcal__day--in-range' : '',
                  inHover ? 'drcal__day--hover-range' : '',
                  isToday ? 'drcal__day--today' : '',
                  disabled ? 'drcal__day--disabled' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => !disabled && handleDayClick(day)}
                onMouseEnter={() => draft.start && !draft.end && setDraft((p) => ({ ...p, hover: day }))}
                disabled={disabled}
              >
                {day.getDate()}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={`filter-drop ${open ? 'filter-drop--open' : ''} ${isActive ? 'filter-drop--active' : ''}`} ref={ref}>
      <button className="filter-drop__trigger" onClick={() => { setOpen((v) => !v); if (!open) setShowCal(isCustom) }}>
        <span className="filter-drop__label">Date range:</span>
        <span className="filter-drop__value">{triggerLabel}</span>
        <CaretDown size={14} className="filter-drop__caret" />
      </button>

      {open && !showCal && (
        <div className="filter-drop__menu">
          {DATE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`filter-drop__option ${value === opt.value ? 'filter-drop__option--selected' : ''}`}
              onClick={() => selectPreset(opt)}
            >
              {opt.label}
              {value === opt.value && <CheckCircle size={16} weight="fill" className="filter-drop__check" />}
            </button>
          ))}
        </div>
      )}

      {open && showCal && (
        <div className="drcal">
          <div className="drcal__header">
            <button className="drcal__nav" onClick={prevMonth}><CaretDown size={14} style={{ transform: 'rotate(90deg)' }} /></button>
            <span />
            <button className="drcal__nav" onClick={nextMonth}><CaretDown size={14} style={{ transform: 'rotate(-90deg)' }} /></button>
          </div>

          <div className="drcal__calendars">
            {renderMonth(leftMonth)}
            {renderMonth(rightMonth)}
          </div>

          <div className="drcal__footer">
            <span className="drcal__hint">
              {!draft.start
                ? 'Select start date'
                : !draft.end
                  ? 'Select end date (max 3 months)'
                  : `${draft.start.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })} – ${draft.end.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}`
              }
            </span>
            <div className="drcal__footer-actions">
              <button className="drcal__back" onClick={() => { setShowCal(false); setDraft({ start: null, end: null, hover: null }) }}>
                ← Back
              </button>
              <button
                className="drcal__apply"
                disabled={!draft.start || !draft.end}
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}